import { z } from 'zod';

import {
  aiDescribeRequestedSchema,
  aiCategorizeRequestedSchema,
  aiSeoRequestedSchema,
} from '../schemas/events/ai-pipeline.js';

import {
  aiPreviewRequestedSchema,
  aiPreviewRespondedSchema,
  aiTestrunRequestedSchema,
  aiTestrunRespondedSchema,
} from '../schemas/events/ai-preview.js';

import {
  retentionRequestedSchema,
  retentionDoneSchema,
} from '../schemas/events/system.js';

import {
  uploadProcessRequestedSchema,
  uploadProcessedSchema,
} from '../schemas/events/upload.js';

import {
  aiInteriorGenerateRequestedSchema,
  wallpapersInteriorDeleteRequestedSchema,
  aiInteriorUpscaleRequestedSchema,
} from '../schemas/events/ai-interior.js';

import {
  realizationUploadRequestedSchema,
  realizationUploadProcessedSchema,
} from '../schemas/events/realization.js';

import {
  customerReviewPhotoUploadRequestedSchema,
  customerReviewPhotoUploadProcessedSchema,
  customerReviewPhotoUpscaleRequestedSchema,
} from '../schemas/events/customer-review-photo.js';

import {
  wallpaperCategorizeCompletedSchema,
  wallpaperTopNAppliedSchema,
  settingsUpdatedSchema,
  wallpaperVariantInvalidatedSchema,
  wallpaperSeoVariantReadySchema,
} from '../schemas/events/category-variants.js';

import {
  translateCategoriesRequestedSchema,
  translateCategoriesStartedSchema,
  translateCategoriesProgressSchema,
  translateCategoriesDoneSchema,
  translateCategoriesFailedSchema,
  translateCategoriesRunStartedSchema,
  translateCategoriesBatchStartedSchema,
  translateCategoriesBatchDoneSchema,
  translateCategoriesBatchFailedSchema,
} from '../schemas/events/translate-categories.js';

import {
  bulkAiRequestedPayloadSchema,
  bulkAiRunStartedPayloadSchema,
  bulkAiBatchStartedPayloadSchema,
  bulkAiBatchDonePayloadSchema,
  bulkAiBatchFailedPayloadSchema,
  bulkAiDonePayloadSchema,
  bulkAiFailedPayloadSchema,
} from '../schemas/events/bulk-ai.js';

import {
  categoryCascadeRequestedSchema,
  categoryCascadeStartedSchema,
  categoryCascadeLevelDoneSchema,
  categoryCascadeFailedSchema,
  categoryCascadeDoneSchema,
} from '../schemas/events/category-cascade.js';

import { runCancelRequestedSchema } from '../schemas/events/run-cancel.js';

import {
  aiModelDiscoveryRequestedSchema,
  aiModelDiscoveryDoneSchema,
  aiModelTestgenRequestedSchema,
  aiModelTestgenRespondedSchema,
} from '../schemas/events/ai-model-discovery.js';

import {
  newsletterConfirmRequestedSchema,
  newsletterWelcomeRequestedSchema,
} from '../schemas/events/newsletter.js';

import {
  favouriteSaleScanSchema,
  favouriteSaleNotifySchema,
} from '../schemas/events/favourite-sale.js';

import {
  evalMatrixRequestedPayloadSchema,
  evalMatrixProgressPayloadSchema,
  evalMatrixDonePayloadSchema,
} from '../eval/types.js';

import {
  blogAuthoringRequestedPayloadSchema,
  blogAuthoringDispatchedPayloadSchema,
  blogAuthoringCompletedPayloadSchema,
  blogAuthoringFailedPayloadSchema,
} from '../schemas/events/blog-authoring.js';

import {
  blogImagesRequestedPayloadSchema,
  blogImagesCompletedPayloadSchema,
  blogImagesFailedPayloadSchema,
  blogImagesPlanRequestedPayloadSchema,
  blogImagesPlanCompletedPayloadSchema,
  blogImagesPlanFailedPayloadSchema,
} from '../schemas/events/blog-images.js';
import {
  blogImagePromptRequestedPayloadSchema,
  blogImagePromptCompletedPayloadSchema,
  blogImagePromptFailedPayloadSchema,
  blogImageRequestedPayloadSchema,
  blogImageCompletedPayloadSchema,
  blogImageFailedPayloadSchema,
} from '../schemas/events/blog-image-single.js';

import {
  interiorTemplateUploadRequestedSchema,
  interiorTemplateUploadProcessedSchema,
  interiorTemplateCompositeRequestedSchema,
} from '../schemas/events/interior-template.js';

/** Constants для всех known event types. Используй вместо строковых литералов. */
export const EVENT = {
  // AI pipeline (linear async). Per-step state is persisted in
  // `wallpaper_ai_steps` (consumed by admin via Realtime) — no ready/failed
  // events emitted, since nobody subscribes to them.
  AI_DESCRIBE_REQUESTED: 'ai.describe.requested',
  AI_CATEGORIZE_REQUESTED: 'ai.categorize.requested',
  AI_SEO_REQUESTED: 'ai.seo.requested',

  // Sync request-response (correlation_id matched)
  AI_PREVIEW_REQUESTED: 'ai.preview.requested',
  AI_PREVIEW_RESPONDED: 'ai.preview.responded',
  AI_TESTRUN_REQUESTED: 'ai.testrun.requested',
  AI_TESTRUN_RESPONDED: 'ai.testrun.responded',

  // System
  RETENTION_REQUESTED: 'retention.requested',
  RETENTION_DONE: 'retention.done',

  // Upload pipeline
  UPLOAD_PROCESS_REQUESTED: 'upload.process.requested',
  UPLOAD_PROCESSED: 'upload.processed',

  // Realization (real customer photo) upload — WS6 T5, distinct from
  // upload.process (which creates a NEW wallpaper row). Attaches a photo to
  // an EXISTING wallpaper via `wallpaper_realizations`.
  REALIZATION_UPLOAD_REQUESTED: 'realization.upload.requested',
  REALIZATION_UPLOAD_PROCESSED: 'realization.upload.processed',

  // Customer-review photo upload — first-party curated gallery (idea
  // 2026-07-12-wandero-customer-photo-reviews-gallery). Sibling of
  // realization.upload but attaches 1—N photos to a `customer_reviews` row.
  // Worker handler + HANDLER_SPECS entry land with T6 (paired). Upscale
  // events are deferred (T6b fast-follow) — schemas exist for forward-compat.
  CUSTOMER_REVIEW_PHOTO_UPLOAD_REQUESTED: 'customer_review_photo.upload.requested',
  CUSTOMER_REVIEW_PHOTO_UPLOAD_PROCESSED: 'customer_review_photo.upload.processed',
  // Customer-review photo upscale (fast-follow, idea
  // 2026-07-12-wandero-reviews-photo-upscale) — mirrors AI_INTERIOR_UPSCALE_REQUESTED
  // but REPLACES `storage_path` in place (no dual-path/`upscale_active` column
  // on `customer_review_photos`). No PROCESSED event — the row's own
  // upscale_status/error columns carry the outcome (Realtime/poll-consumed).
  CUSTOMER_REVIEW_PHOTO_UPSCALE_REQUESTED: 'customer_review_photo.upscale.requested',

  // AI interiors (admin → worker)
  AI_INTERIOR_GENERATE_REQUESTED: 'ai.interior.generate.requested',
  WALLPAPERS_INTERIOR_DELETE_REQUESTED: 'wallpapers.interior.delete.requested',
  // Interior upscale (fal clarity/aura-sr/recraft-crisp), structural sibling
  // of AI_INTERIOR_GENERATE_REQUESTED — idea 2026-07-04-wandero-interior-upscale-fal-clarity
  AI_INTERIOR_UPSCALE_REQUESTED: 'ai.interior.upscale.requested',

  // Bulk translate categories_i18n (admin → worker)
  TRANSLATE_CATEGORIES_REQUESTED: 'ai.translate_categories.requested',
  TRANSLATE_CATEGORIES_STARTED: 'ai.translate_categories.started',
  TRANSLATE_CATEGORIES_PROGRESS: 'ai.translate_categories.progress',
  TRANSLATE_CATEGORIES_DONE: 'ai.translate_categories.done',
  TRANSLATE_CATEGORIES_FAILED: 'ai.translate_categories.failed',
  // Dot-named richer per-run / per-batch events (SPEC §13).
  TRANSLATE_CATEGORIES_RUN_STARTED: 'translate_categories.run.started',
  TRANSLATE_CATEGORIES_BATCH_STARTED: 'translate_categories.batch.started',
  TRANSLATE_CATEGORIES_BATCH_DONE: 'translate_categories.batch.done',
  TRANSLATE_CATEGORIES_BATCH_FAILED: 'translate_categories.batch.failed',

  // Bulk AI operations (PR-2): unified event schema for all bulk scenarios
  BULK_AI_REQUESTED: 'bulk_ai.requested',
  BULK_AI_STARTED: 'bulk_ai.started',
  BULK_AI_PROGRESS: 'bulk_ai.progress',
  BULK_AI_RUN_STARTED: 'bulk_ai.run.started',
  BULK_AI_BATCH_STARTED: 'bulk_ai.batch.started',
  BULK_AI_BATCH_DONE: 'bulk_ai.batch.done',
  BULK_AI_BATCH_FAILED: 'bulk_ai.batch.failed',
  BULK_AI_DONE: 'bulk_ai.done',
  BULK_AI_FAILED: 'bulk_ai.failed',

  // Single-category cascade generation (admin → worker). Generates every content
  // property of ONE category on ONE language in dependency order (keywords →
  // consumers → their consumers), one LLM call per level, persisted between
  // levels. idea 2026-07-17-category-cascade-generation.
  CATEGORY_CASCADE_REQUESTED: 'category_cascade.requested',
  CATEGORY_CASCADE_STARTED: 'category_cascade.started',
  CATEGORY_CASCADE_LEVEL_DONE: 'category_cascade.level.done',
  CATEGORY_CASCADE_FAILED: 'category_cascade.failed',
  CATEGORY_CASCADE_DONE: 'category_cascade.done',

  // Control-plane events — consumed by the reactor itself, never claimed as jobs
  RUN_CANCEL_REQUESTED: 'run.cancel.requested',

  // AI model discovery (fal.ai model scanning + dry-run testgen)
  AI_MODEL_DISCOVERY_REQUESTED: 'ai_model.discovery.requested',
  AI_MODEL_DISCOVERY_DONE: 'ai_model.discovery.done',
  AI_MODEL_TESTGEN_REQUESTED: 'ai_model.testgen.requested',
  AI_MODEL_TESTGEN_RESPONDED: 'ai_model.testgen.responded',

  // Category variants pipeline (A4+, A5)
  // wallpaper.categorize.completed — emitted by ai-categorize handler after categorize
  WALLPAPER_CATEGORIZE_COMPLETED: 'wallpaper.categorize.completed',
  // wallpaper.top_n.applied — emitted by top-n-applier after diff/apply
  WALLPAPER_TOP_N_APPLIED: 'wallpaper.top_n.applied',
  // settings.updated — emitted by update_app_setting RPC (DB-side)
  SETTINGS_UPDATED: 'settings.updated',
  // wallpaper.variant.invalidated — emitted by ai-seo-variant (fan-out) and prompt-invalidator
  WALLPAPER_VARIANT_INVALIDATED: 'wallpaper.variant.invalidated',
  // wallpaper.seo.variant_ready — emitted by ai-seo-variant after successful UPDATE
  WALLPAPER_SEO_VARIANT_READY: 'wallpaper.seo.variant_ready',

  // Newsletter double-opt-in flow
  NEWSLETTER_CONFIRM_REQUESTED: 'newsletter.confirm.requested',
  NEWSLETTER_WELCOME_REQUESTED: 'newsletter.welcome.requested',

  // Favourite-sale scan and notify
  FAVOURITE_SALE_SCAN: 'favourite-sale.scan',
  FAVOURITE_SALE_NOTIFY: 'favourite-sale.notify',

  // Eval matrix (admin → worker, sandbox — never writes categories_i18n)
  EVAL_MATRIX_REQUESTED: 'eval.matrix.requested',
  EVAL_MATRIX_PROGRESS: 'eval.matrix.progress',
  EVAL_MATRIX_DONE: 'eval.matrix.done',

  // Blog AI-authoring (P3): admin brief → dotclaude content-writer (agent
  // dispatch + signed webhook, NOT poll-based) → draft body populated.
  BLOG_AUTHORING_REQUESTED: 'blog.authoring.requested',
  BLOG_AUTHORING_DISPATCHED: 'blog.authoring.dispatched',
  BLOG_AUTHORING_COMPLETED: 'blog.authoring.completed',
  BLOG_AUTHORING_FAILED: 'blog.authoring.failed',

  // Blog article AI-images (maira 2-stage pattern): admin → worker, ONE
  // handler runs enhance (LLM) → generate (ImageRouter) → insert → persist
  // synchronously, no dispatch/webhook round-trip. idea
  // 2026-07-19-blog-article-images-like-maira.
  BLOG_IMAGES_REQUESTED: 'blog.images.requested',
  BLOG_IMAGES_COMPLETED: 'blog.images.completed',
  BLOG_IMAGES_FAILED: 'blog.images.failed',

  // Batch plan/approve (AC v2, 2026-07-27): the enhance stage alone, so the
  // author reviews and edits the prompts BEFORE any paid generation runs.
  BLOG_IMAGES_PLAN_REQUESTED: 'blog.images.plan.requested',
  BLOG_IMAGES_PLAN_COMPLETED: 'blog.images.plan.completed',
  BLOG_IMAGES_PLAN_FAILED: 'blog.images.plan.failed',

  // Editor-toolbar single-image flow (AC v2, maira AiImageModal parity):
  // stage 1 expands the author's rough idea into an editable EN prompt,
  // stage 2 generates ONE image and returns only its URL — the admin inserts
  // it at the cursor, so no body_html is written worker-side.
  BLOG_IMAGE_PROMPT_REQUESTED: 'blog.image_prompt.requested',
  BLOG_IMAGE_PROMPT_COMPLETED: 'blog.image_prompt.completed',
  BLOG_IMAGE_PROMPT_FAILED: 'blog.image_prompt.failed',
  BLOG_IMAGE_REQUESTED: 'blog.image.requested',
  BLOG_IMAGE_COMPLETED: 'blog.image.completed',
  BLOG_IMAGE_FAILED: 'blog.image.failed',

  // Interior templates: transparent-cutout PNG upload + quad-detect (sync),
  // and wallpaper→template perspective-warp composite (fire-and-forget,
  // Realtime-observed on wallpapers_interiors). idea
  // 2026-07-17-interior-templates-cutout.
  INTERIOR_TEMPLATE_UPLOAD_REQUESTED: 'interior_template.upload.requested',
  INTERIOR_TEMPLATE_UPLOAD_PROCESSED: 'interior_template.upload.processed',
  INTERIOR_TEMPLATE_COMPOSITE_REQUESTED: 'interior_template.composite.requested',
} as const;

export type EventType = (typeof EVENT)[keyof typeof EVENT];

/** Mapping event type → zod schema for runtime validation. */
export const EVENT_SCHEMAS = {
  [EVENT.AI_DESCRIBE_REQUESTED]: aiDescribeRequestedSchema,
  [EVENT.AI_CATEGORIZE_REQUESTED]: aiCategorizeRequestedSchema,
  [EVENT.AI_SEO_REQUESTED]: aiSeoRequestedSchema,

  [EVENT.AI_PREVIEW_REQUESTED]: aiPreviewRequestedSchema,
  [EVENT.AI_PREVIEW_RESPONDED]: aiPreviewRespondedSchema,
  [EVENT.AI_TESTRUN_REQUESTED]: aiTestrunRequestedSchema,
  [EVENT.AI_TESTRUN_RESPONDED]: aiTestrunRespondedSchema,

  [EVENT.RETENTION_REQUESTED]: retentionRequestedSchema,
  [EVENT.RETENTION_DONE]: retentionDoneSchema,

  [EVENT.UPLOAD_PROCESS_REQUESTED]: uploadProcessRequestedSchema,
  [EVENT.UPLOAD_PROCESSED]: uploadProcessedSchema,

  [EVENT.REALIZATION_UPLOAD_REQUESTED]: realizationUploadRequestedSchema,
  [EVENT.REALIZATION_UPLOAD_PROCESSED]: realizationUploadProcessedSchema,

  [EVENT.CUSTOMER_REVIEW_PHOTO_UPLOAD_REQUESTED]: customerReviewPhotoUploadRequestedSchema,
  [EVENT.CUSTOMER_REVIEW_PHOTO_UPLOAD_PROCESSED]: customerReviewPhotoUploadProcessedSchema,
  [EVENT.CUSTOMER_REVIEW_PHOTO_UPSCALE_REQUESTED]: customerReviewPhotoUpscaleRequestedSchema,

  [EVENT.AI_INTERIOR_GENERATE_REQUESTED]: aiInteriorGenerateRequestedSchema,
  [EVENT.WALLPAPERS_INTERIOR_DELETE_REQUESTED]: wallpapersInteriorDeleteRequestedSchema,
  [EVENT.AI_INTERIOR_UPSCALE_REQUESTED]: aiInteriorUpscaleRequestedSchema,

  [EVENT.TRANSLATE_CATEGORIES_REQUESTED]: translateCategoriesRequestedSchema,
  [EVENT.TRANSLATE_CATEGORIES_STARTED]: translateCategoriesStartedSchema,
  [EVENT.TRANSLATE_CATEGORIES_PROGRESS]: translateCategoriesProgressSchema,
  [EVENT.TRANSLATE_CATEGORIES_DONE]: translateCategoriesDoneSchema,
  [EVENT.TRANSLATE_CATEGORIES_FAILED]: translateCategoriesFailedSchema,
  [EVENT.TRANSLATE_CATEGORIES_RUN_STARTED]: translateCategoriesRunStartedSchema,
  [EVENT.TRANSLATE_CATEGORIES_BATCH_STARTED]: translateCategoriesBatchStartedSchema,
  [EVENT.TRANSLATE_CATEGORIES_BATCH_DONE]: translateCategoriesBatchDoneSchema,
  [EVENT.TRANSLATE_CATEGORIES_BATCH_FAILED]: translateCategoriesBatchFailedSchema,

  [EVENT.BULK_AI_REQUESTED]: bulkAiRequestedPayloadSchema,
  [EVENT.BULK_AI_STARTED]: z.object({}),
  [EVENT.BULK_AI_PROGRESS]: z.object({}),
  [EVENT.BULK_AI_RUN_STARTED]: bulkAiRunStartedPayloadSchema,
  [EVENT.BULK_AI_BATCH_STARTED]: bulkAiBatchStartedPayloadSchema,
  [EVENT.BULK_AI_BATCH_DONE]: bulkAiBatchDonePayloadSchema,
  [EVENT.BULK_AI_BATCH_FAILED]: bulkAiBatchFailedPayloadSchema,
  [EVENT.BULK_AI_DONE]: bulkAiDonePayloadSchema,
  [EVENT.BULK_AI_FAILED]: bulkAiFailedPayloadSchema,

  [EVENT.CATEGORY_CASCADE_REQUESTED]: categoryCascadeRequestedSchema,
  [EVENT.CATEGORY_CASCADE_STARTED]: categoryCascadeStartedSchema,
  [EVENT.CATEGORY_CASCADE_LEVEL_DONE]: categoryCascadeLevelDoneSchema,
  [EVENT.CATEGORY_CASCADE_FAILED]: categoryCascadeFailedSchema,
  [EVENT.CATEGORY_CASCADE_DONE]: categoryCascadeDoneSchema,

  [EVENT.RUN_CANCEL_REQUESTED]: runCancelRequestedSchema,

  [EVENT.WALLPAPER_CATEGORIZE_COMPLETED]: wallpaperCategorizeCompletedSchema,
  [EVENT.WALLPAPER_TOP_N_APPLIED]: wallpaperTopNAppliedSchema,
  [EVENT.SETTINGS_UPDATED]: settingsUpdatedSchema,
  [EVENT.WALLPAPER_VARIANT_INVALIDATED]: wallpaperVariantInvalidatedSchema,
  [EVENT.WALLPAPER_SEO_VARIANT_READY]: wallpaperSeoVariantReadySchema,

  [EVENT.AI_MODEL_DISCOVERY_REQUESTED]: aiModelDiscoveryRequestedSchema,
  [EVENT.AI_MODEL_DISCOVERY_DONE]: aiModelDiscoveryDoneSchema,
  [EVENT.AI_MODEL_TESTGEN_REQUESTED]: aiModelTestgenRequestedSchema,
  [EVENT.AI_MODEL_TESTGEN_RESPONDED]: aiModelTestgenRespondedSchema,

  [EVENT.NEWSLETTER_CONFIRM_REQUESTED]: newsletterConfirmRequestedSchema,
  [EVENT.NEWSLETTER_WELCOME_REQUESTED]: newsletterWelcomeRequestedSchema,

  [EVENT.FAVOURITE_SALE_SCAN]: favouriteSaleScanSchema,
  [EVENT.FAVOURITE_SALE_NOTIFY]: favouriteSaleNotifySchema,

  [EVENT.EVAL_MATRIX_REQUESTED]: evalMatrixRequestedPayloadSchema,
  [EVENT.EVAL_MATRIX_PROGRESS]: evalMatrixProgressPayloadSchema,
  [EVENT.EVAL_MATRIX_DONE]: evalMatrixDonePayloadSchema,

  [EVENT.BLOG_AUTHORING_REQUESTED]: blogAuthoringRequestedPayloadSchema,
  [EVENT.BLOG_AUTHORING_DISPATCHED]: blogAuthoringDispatchedPayloadSchema,
  [EVENT.BLOG_AUTHORING_COMPLETED]: blogAuthoringCompletedPayloadSchema,
  [EVENT.BLOG_AUTHORING_FAILED]: blogAuthoringFailedPayloadSchema,

  [EVENT.BLOG_IMAGES_REQUESTED]: blogImagesRequestedPayloadSchema,
  [EVENT.BLOG_IMAGES_COMPLETED]: blogImagesCompletedPayloadSchema,
  [EVENT.BLOG_IMAGES_FAILED]: blogImagesFailedPayloadSchema,

  [EVENT.BLOG_IMAGES_PLAN_REQUESTED]: blogImagesPlanRequestedPayloadSchema,
  [EVENT.BLOG_IMAGES_PLAN_COMPLETED]: blogImagesPlanCompletedPayloadSchema,
  [EVENT.BLOG_IMAGES_PLAN_FAILED]: blogImagesPlanFailedPayloadSchema,

  [EVENT.BLOG_IMAGE_PROMPT_REQUESTED]: blogImagePromptRequestedPayloadSchema,
  [EVENT.BLOG_IMAGE_PROMPT_COMPLETED]: blogImagePromptCompletedPayloadSchema,
  [EVENT.BLOG_IMAGE_PROMPT_FAILED]: blogImagePromptFailedPayloadSchema,
  [EVENT.BLOG_IMAGE_REQUESTED]: blogImageRequestedPayloadSchema,
  [EVENT.BLOG_IMAGE_COMPLETED]: blogImageCompletedPayloadSchema,
  [EVENT.BLOG_IMAGE_FAILED]: blogImageFailedPayloadSchema,

  [EVENT.INTERIOR_TEMPLATE_UPLOAD_REQUESTED]: interiorTemplateUploadRequestedSchema,
  [EVENT.INTERIOR_TEMPLATE_UPLOAD_PROCESSED]: interiorTemplateUploadProcessedSchema,
  [EVENT.INTERIOR_TEMPLATE_COMPOSITE_REQUESTED]: interiorTemplateCompositeRequestedSchema,
} as const satisfies Record<EventType, z.ZodTypeAny>;

/** Type-safe payload parser. Throws ZodError on invalid payload. */
export function parseEventPayload<T extends EventType>(
  type: T,
  payload: unknown,
): z.infer<(typeof EVENT_SCHEMAS)[T]> {
  const schema = EVENT_SCHEMAS[type];
  return schema.parse(payload) as z.infer<(typeof EVENT_SCHEMAS)[T]>;
}

/** Safe variant — returns Result-like { success, data?|error? }. */
export function safeParseEventPayload<T extends EventType>(
  type: T,
  payload: unknown,
):
  | { success: true; data: z.infer<(typeof EVENT_SCHEMAS)[T]> }
  | { success: false; error: z.ZodError } {
  const schema = EVENT_SCHEMAS[type];
  const r = schema.safeParse(payload);
  if (r.success) return { success: true, data: r.data as z.infer<(typeof EVENT_SCHEMAS)[T]> };
  return { success: false, error: r.error };
}
